import { Alert, SkeletonRows, StatusBadge, Tabs, TabsContent, TabsList, TabsTrigger } from "@station/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useFleetAccount } from "../../../features/fleet/queries.js";
import { AccountStatusBadge, FleetPage, fleetCrumb } from "../../../features/fleet/shared.js";
import { DriversTab, TagsTab, VehiclesTab } from "../../../features/fleet/tabs/assets-tabs.js";
import { BillingTab } from "../../../features/fleet/tabs/billing-tab.js";
import { GeneralTab } from "../../../features/fleet/tabs/general-tab.js";
import { PortalUsersTab } from "../../../features/fleet/tabs/portal-users-tab.js";
import { RulesTab } from "../../../features/fleet/tabs/rules-tab.js";
import { StatementTab, UsageTab } from "../../../features/fleet/tabs/usage-tabs.js";
import { errorMessage } from "../../../lib/errors.js";

const TABS = [
  "general",
  "rules",
  "vehicles",
  "drivers",
  "tags",
  "usage",
  "statement",
  "billing",
  "portal",
] as const;
type TabKey = (typeof TABS)[number];

const searchSchema = z.object({
  tab: z.enum(TABS).optional().catch(undefined),
  vehicle: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/filo/$counterpartyId")({
  validateSearch: (search) => searchSchema.parse(search),
  component: FleetAccountRoute,
});

/**
 * Filo hesabı ayrıntısı (Prompt 17 web): genel, kurallar (cari varsayılanı + araç geçersiz kılması + yetkilendirme
 * önizlemesi), araçlar, sürücüler, TTS etiketleri, kullanım defteri, ekstre, dönem faturaları ve portal kullanıcıları.
 * Sekme `?tab=` ile adreslenir (paylaşılabilir bağlantı); içerik yalnızca seçili sekmede çizilir.
 */
function FleetAccountRoute(): ReactElement {
  const { t } = useTranslation();
  const { counterpartyId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const detail = useFleetAccount(counterpartyId);
  const tab: TabKey = search.tab ?? "general";
  const title = detail.data
    ? `${detail.data.counterparty.code} · ${detail.data.counterparty.name}`
    : t("menu.fleetAccounts");

  return (
    <FleetPage
      title={title}
      breadcrumb={[fleetCrumb(t("menu.fleetAccounts")), { label: detail.data?.counterparty.code ?? "…" }]}
    >
      {detail.error ? (
        <Alert tone="danger">{errorMessage(detail.error)}</Alert>
      ) : !detail.data ? (
        <SkeletonRows rows={8} />
      ) : (
        <div className="grid gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <AccountStatusBadge status={detail.data.account?.status ?? null} />
            {detail.data.counterparty.isBlocked ? (
              <StatusBadge tone="danger">{t("fleet.accounts.blocked")}</StatusBadge>
            ) : null}
            {detail.data.counterparty.isActive ? null : (
              <StatusBadge tone="neutral">{t("common.inactive")}</StatusBadge>
            )}
          </div>
          <Tabs
            value={tab}
            onValueChange={(next) => void navigate({ search: { tab: next as TabKey }, replace: true })}
          >
            <TabsList aria-label={t("fleet.detail.tabsLabel")} className="h-auto flex-wrap">
              {TABS.map((key) => (
                <TabsTrigger key={key} value={key}>
                  {t(`fleet.tabs.${key}`)}
                </TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value="general">
              {tab === "general" ? <GeneralTab detail={detail.data} /> : null}
            </TabsContent>
            <TabsContent value="rules">
              {tab === "rules" ? <RulesTab detail={detail.data} /> : null}
            </TabsContent>
            <TabsContent value="vehicles">
              {tab === "vehicles" ? <VehiclesTab detail={detail.data} /> : null}
            </TabsContent>
            <TabsContent value="drivers">
              {tab === "drivers" ? <DriversTab detail={detail.data} /> : null}
            </TabsContent>
            <TabsContent value="tags">{tab === "tags" ? <TagsTab detail={detail.data} /> : null}</TabsContent>
            <TabsContent value="usage">
              {tab === "usage" ? <UsageTab detail={detail.data} /> : null}
            </TabsContent>
            <TabsContent value="statement">
              {tab === "statement" ? <StatementTab detail={detail.data} /> : null}
            </TabsContent>
            <TabsContent value="billing">
              {tab === "billing" ? <BillingTab detail={detail.data} /> : null}
            </TabsContent>
            <TabsContent value="portal">
              {tab === "portal" ? <PortalUsersTab detail={detail.data} /> : null}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </FleetPage>
  );
}
